// Builds a short, readable campaign name from the campaign strategy (campaign_goals.v2).
export const MAX_CAMPAIGN_NAME = 60;
export const LEGACY_DEFAULT_NAMES = ["My First Campaign", ""];

type Fields = Record<string, unknown>;
export interface AutoNameInput {
  goal?: string;
  goalFields?: Fields;
  goalCustom?: string;
  products?: string[];
  titles?: string[];
  industries?: string[];
  countries?: string[];
}

const str = (v: unknown): string => {
  if (Array.isArray(v)) return v.map(String).filter(Boolean)[0] ?? "";
  return typeof v === "string" ? v.trim() : "";
};
const list = (v: unknown, n = 2): string => (Array.isArray(v) ? v.map(String).filter(Boolean).slice(0, n).join(" & ") : str(v));
const join = (...parts: string[]) => parts.map((p) => p.trim()).filter(Boolean).join(" – ");

export const clampName = (name: string, max = MAX_CAMPAIGN_NAME): string => {
  const n = name.replace(/\s+/g, " ").trim();
  if (n.length <= max) return n;
  const cut = n.slice(0, max);
  const at = Math.max(cut.lastIndexOf(" – "), cut.lastIndexOf(" "));
  return (at > max * 0.5 ? cut.slice(0, at) : cut).replace(/[\s–:&,-]+$/, "").trim();
};

export function buildCampaignName(i: AutoNameInput): string {
  const f = i.goalFields ?? {};
  const place = list(i.countries, 2);
  const who = list(i.titles, 2);
  let name = "";
  switch (i.goal) {
    case "investors":
    case "fundraise":
    case "funding":
    case "raise":
      name = join(`${str(f.stage) || "Funding"} Raise`, list(f.investor_type, 2) ? `${list(f.investor_type, 2)} Investors` : who || "Investors");
      break;
    case "hire":
    case "hiring":
      name = join(`Hiring: ${str(f.role) || who || "Talent"}`, str(f.location) || place);
      break;
    case "event":
      name = join(`Event: ${str(f.event_name) || "Invites"}`, str(f.event_name) ? "Invites" : "", place);
      break;
    case "network":
    case "networking":
      name = join(`Networking: ${list(f.meet, 2) || who || "Peers"}`, place);
      break;
    case "job":
    case "job_search":
      name = join(`Job Search: ${str(f.role) || "Opportunities"}`, place);
      break;
    case "partner":
    case "partners":
      name = join(`${list(f.partner_type, 1) || "Partner"} Outreach`, list(i.products, 1), place);
      break;
    case "other":
      name = join(i.goalCustom || who || "Outreach", place);
      break;
    default:
      name = join(list(i.products, 1) || list(i.industries, 1) || "Outreach", who, place);
  }
  return clampName(name || "New Campaign");
}

export function nameFromCampaignGoals(goals: any): string | null {
  const v2 = goals?.v2;
  if (!v2) return null;
  const c = v2.confirmed ?? {};
  return buildCampaignName({
    goal: c.goal || "sell",
    goalFields: c.goal_fields,
    goalCustom: c.goal_custom,
    products: Array.isArray(c.products) && c.products.length ? c.products : [v2.product].filter(Boolean),
    titles: Array.isArray(c.buyer_titles) && c.buyer_titles.length ? c.buyer_titles : v2.buyer_titles,
    industries: v2.buyer_industries,
    countries: c.countries,
  });
}

export function dedupeName(name: string, existing: string[]): string {
  const taken = new Set(existing.map((n) => n.trim().toLowerCase()));
  if (!taken.has(name.toLowerCase())) return name;
  for (let k = 2; k < 100; k++) {
    const suffix = ` (${k})`;
    const candidate = clampName(name, MAX_CAMPAIGN_NAME - suffix.length) + suffix;
    if (!taken.has(candidate.toLowerCase())) return candidate;
  }
  return name;
}
